import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Fee,
  FeeCategory,
  Student,
  Parent,
  Course,
  SchoolYear,
  Order,
  OrderItem,
  Questionnaire,
  QuestionnaireField,
  Notification,
  PreAssessedFee,
  Admin,
  AdminUser,
  CommunityMember,
  FeeDisclaimer,
} from '../types';
import { mockFees } from '../mock/fees';
import { mockFeeCategories } from '../mock/feeCategories';
import { mockStudents } from '../mock/students';
import { mockParents } from '../mock/parents';
import { mockCourses } from '../mock/courses';
import { mockSchoolYears } from '../mock/schoolYears';
import { mockOrders, mockOrderItems } from '../mock/orders';
import { mockQuestionnaires, mockQuestionnaireFields } from '../mock/questionnaires';
import { mockNotifications } from '../mock/notifications';
import { mockPreAssessedFees } from '../mock/preAssessedFees';
import { mockAdmins } from '../mock/admins';
import { mockAdminUsers } from '../mock/adminUsers';
import { mockCommunityMembers } from '../mock/communityMembers';
import { mockFeeDisclaimers } from '../mock/feeDisclaimers';

interface DataContextType {
  fees: Fee[];
  setFees: React.Dispatch<React.SetStateAction<Fee[]>>;
  addFee: (fee: Fee) => void;
  updateFee: (fee: Fee) => void;
  deleteFee: (feeId: string) => void;

  feeCategories: FeeCategory[];
  setFeeCategories: React.Dispatch<React.SetStateAction<FeeCategory[]>>;
  addFeeCategory: (category: FeeCategory) => void;
  updateFeeCategory: (category: FeeCategory) => void;
  deleteFeeCategory: (categoryId: string) => void;

  students: Student[];
  setStudents: React.Dispatch<React.SetStateAction<Student[]>>;
  addStudent: (student: Student) => void;
  updateStudent: (student: Student) => void;
  deleteStudent: (studentId: string) => void;

  parents: Parent[];
  setParents: React.Dispatch<React.SetStateAction<Parent[]>>;
  addParent: (parent: Parent) => void;
  updateParent: (parent: Parent) => void;
  deleteParent: (parentId: string) => void;

  courses: Course[];
  setCourses: React.Dispatch<React.SetStateAction<Course[]>>;
  addCourse: (course: Course) => void;
  updateCourse: (course: Course) => void;
  deleteCourse: (courseId: string) => void;

  schoolYears: SchoolYear[];
  setSchoolYears: React.Dispatch<React.SetStateAction<SchoolYear[]>>;
  addSchoolYear: (schoolYear: SchoolYear) => void;
  updateSchoolYear: (schoolYear: SchoolYear) => void;
  deleteSchoolYear: (schoolYearId: string) => void;

  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  addOrder: (order: Order) => void;
  updateOrder: (order: Order) => void;

  orderItems: OrderItem[];
  setOrderItems: React.Dispatch<React.SetStateAction<OrderItem[]>>;

  questionnaires: Questionnaire[];
  setQuestionnaires: React.Dispatch<React.SetStateAction<Questionnaire[]>>;
  addQuestionnaire: (questionnaire: Questionnaire) => void;
  updateQuestionnaire: (questionnaire: Questionnaire) => void;
  deleteQuestionnaire: (questionnaireId: string) => void;

  questionnaireFields: QuestionnaireField[];
  setQuestionnaireFields: React.Dispatch<React.SetStateAction<QuestionnaireField[]>>;
  addQuestionnaireField: (field: QuestionnaireField) => void;
  updateQuestionnaireField: (field: QuestionnaireField) => void;
  deleteQuestionnaireField: (fieldId: string) => void;

  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  addNotification: (notification: Notification) => void;
  updateNotification: (notification: Notification) => void;
  deleteNotification: (notificationId: string) => void;

  preAssessedFees: PreAssessedFee[];
  setPreAssessedFees: React.Dispatch<React.SetStateAction<PreAssessedFee[]>>;
  addPreAssessedFee: (preAssessedFee: PreAssessedFee) => void;
  updatePreAssessedFee: (preAssessedFee: PreAssessedFee) => void;
  deletePreAssessedFee: (preAssessedFeeId: string) => void;

  admins: Admin[];
  setAdmins: React.Dispatch<React.SetStateAction<Admin[]>>;

  adminUsers: AdminUser[];
  setAdminUsers: React.Dispatch<React.SetStateAction<AdminUser[]>>;
  addAdminUser: (adminUser: AdminUser) => void;
  updateAdminUser: (adminUser: AdminUser) => void;
  deleteAdminUser: (adminUserId: string) => void;

  communityMembers: CommunityMember[];
  setCommunityMembers: React.Dispatch<React.SetStateAction<CommunityMember[]>>;
  addCommunityMember: (member: CommunityMember) => void;
  updateCommunityMember: (member: CommunityMember) => void;
  deleteCommunityMember: (memberId: string) => void;

  feeDisclaimers: FeeDisclaimer[];
  setFeeDisclaimers: React.Dispatch<React.SetStateAction<FeeDisclaimer[]>>;
  addFeeDisclaimer: (disclaimer: FeeDisclaimer) => void;
  updateFeeDisclaimer: (disclaimer: FeeDisclaimer) => void;
  deleteFeeDisclaimer: (disclaimerId: string) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [fees, setFees] = useState<Fee[]>(mockFees);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>(mockFeeCategories);
  const [students, setStudents] = useState<Student[]>(mockStudents);
  const [parents, setParents] = useState<Parent[]>(mockParents);
  const [courses, setCourses] = useState<Course[]>(mockCourses);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>(mockSchoolYears);
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [orderItems, setOrderItems] = useState<OrderItem[]>(mockOrderItems);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>(mockQuestionnaires);
  const [questionnaireFields, setQuestionnaireFields] = useState<QuestionnaireField[]>(mockQuestionnaireFields);
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [preAssessedFees, setPreAssessedFees] = useState<PreAssessedFee[]>(mockPreAssessedFees);
  const [admins, setAdmins] = useState<Admin[]>(mockAdmins);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>(mockAdminUsers);
  const [communityMembers, setCommunityMembers] = useState<CommunityMember[]>(mockCommunityMembers);
  const [feeDisclaimers, setFeeDisclaimers] = useState<FeeDisclaimer[]>(mockFeeDisclaimers);

  const addFee = useCallback((fee: Fee) => {
    setFees(prev => [...prev, fee]);
  }, []);

  const updateFee = useCallback((fee: Fee) => {
    setFees(prev => prev.map(f => f.id === fee.id ? fee : f));
  }, []);

  const deleteFee = useCallback((feeId: string) => {
    setFees(prev => prev.filter(f => f.id !== feeId));
  }, []);

  const addFeeCategory = useCallback((category: FeeCategory) => {
    setFeeCategories(prev => [...prev, category]);
  }, []);

  const updateFeeCategory = useCallback((category: FeeCategory) => {
    setFeeCategories(prev => prev.map(c => c.id === category.id ? category : c));
  }, []);

  const deleteFeeCategory = useCallback((categoryId: string) => {
    setFeeCategories(prev => prev.filter(c => c.id !== categoryId));
  }, []);

  const addStudent = useCallback((student: Student) => {
    setStudents(prev => [...prev, student]);
  }, []);

  const updateStudent = useCallback((student: Student) => {
    setStudents(prev => prev.map(s => s.id === student.id ? student : s));
  }, []);

  const deleteStudent = useCallback((studentId: string) => {
    setStudents(prev => prev.filter(s => s.id !== studentId));
  }, []);

  const addParent = useCallback((parent: Parent) => {
    setParents(prev => [...prev, parent]);
  }, []);

  const updateParent = useCallback((parent: Parent) => {
    setParents(prev => prev.map(p => p.id === parent.id ? parent : p));
  }, []);

  const deleteParent = useCallback((parentId: string) => {
    setParents(prev => prev.filter(p => p.id !== parentId));
  }, []);

  const addCourse = useCallback((course: Course) => {
    setCourses(prev => [...prev, course]);
  }, []);

  const updateCourse = useCallback((course: Course) => {
    setCourses(prev => prev.map(c => c.id === course.id ? course : c));
  }, []);

  const deleteCourse = useCallback((courseId: string) => {
    setCourses(prev => prev.filter(c => c.id !== courseId));
  }, []);

  const addSchoolYear = useCallback((schoolYear: SchoolYear) => {
    setSchoolYears(prev => [...prev, schoolYear]);
  }, []);

  const updateSchoolYear = useCallback((schoolYear: SchoolYear) => {
    setSchoolYears(prev => prev.map(sy => sy.id === schoolYear.id ? schoolYear : sy));
  }, []);

  const deleteSchoolYear = useCallback((schoolYearId: string) => {
    setSchoolYears(prev => prev.filter(sy => sy.id !== schoolYearId));
  }, []);

  const addOrder = useCallback((order: Order) => {
    setOrders(prev => [...prev, order]);
  }, []);

  const updateOrder = useCallback((order: Order) => {
    setOrders(prev => prev.map(o => o.id === order.id ? order : o));
  }, []);

  const addQuestionnaire = useCallback((questionnaire: Questionnaire) => {
    setQuestionnaires(prev => [...prev, questionnaire]);
  }, []);

  const updateQuestionnaire = useCallback((questionnaire: Questionnaire) => {
    setQuestionnaires(prev => prev.map(q => q.id === questionnaire.id ? questionnaire : q));
  }, []);

  const deleteQuestionnaire = useCallback((questionnaireId: string) => {
    setQuestionnaires(prev => prev.filter(q => q.id !== questionnaireId));
  }, []);

  const addQuestionnaireField = useCallback((field: QuestionnaireField) => {
    setQuestionnaireFields(prev => [...prev, field]);
  }, []);

  const updateQuestionnaireField = useCallback((field: QuestionnaireField) => {
    setQuestionnaireFields(prev => prev.map(f => f.id === field.id ? field : f));
  }, []);

  const deleteQuestionnaireField = useCallback((fieldId: string) => {
    setQuestionnaireFields(prev => prev.filter(f => f.id !== fieldId));
  }, []);

  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => [...prev, notification]);
  }, []);

  const updateNotification = useCallback((notification: Notification) => {
    setNotifications(prev => prev.map(n => n.id === notification.id ? notification : n));
  }, []);

  const deleteNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const addPreAssessedFee = useCallback((preAssessedFee: PreAssessedFee) => {
    setPreAssessedFees(prev => [...prev, preAssessedFee]);
  }, []);

  const updatePreAssessedFee = useCallback((preAssessedFee: PreAssessedFee) => {
    setPreAssessedFees(prev => prev.map(pf => pf.id === preAssessedFee.id ? preAssessedFee : pf));
  }, []);

  const deletePreAssessedFee = useCallback((preAssessedFeeId: string) => {
    setPreAssessedFees(prev => prev.filter(pf => pf.id !== preAssessedFeeId));
  }, []);

  const addAdminUser = useCallback((adminUser: AdminUser) => {
    setAdminUsers(prev => [...prev, adminUser]);
  }, []);

  const updateAdminUser = useCallback((adminUser: AdminUser) => {
    setAdminUsers(prev => prev.map(au => au.id === adminUser.id ? adminUser : au));
  }, []);

  const deleteAdminUser = useCallback((adminUserId: string) => {
    setAdminUsers(prev => prev.filter(au => au.id !== adminUserId));
  }, []);

  const addCommunityMember = useCallback((member: CommunityMember) => {
    setCommunityMembers(prev => [...prev, member]);
  }, []);

  const updateCommunityMember = useCallback((member: CommunityMember) => {
    setCommunityMembers(prev => prev.map(m => m.id === member.id ? member : m));
  }, []);

  const deleteCommunityMember = useCallback((memberId: string) => {
    setCommunityMembers(prev => prev.filter(m => m.id !== memberId));
  }, []);

  const addFeeDisclaimer = useCallback((disclaimer: FeeDisclaimer) => {
    setFeeDisclaimers(prev => [...prev, disclaimer]);
  }, []);

  const updateFeeDisclaimer = useCallback((disclaimer: FeeDisclaimer) => {
    setFeeDisclaimers(prev => prev.map(d => d.id === disclaimer.id ? disclaimer : d));
  }, []);

  const deleteFeeDisclaimer = useCallback((disclaimerId: string) => {
    setFeeDisclaimers(prev => prev.filter(d => d.id !== disclaimerId));
  }, []);

  const value: DataContextType = {
    fees,
    setFees,
    addFee,
    updateFee,
    deleteFee,

    feeCategories,
    setFeeCategories,
    addFeeCategory,
    updateFeeCategory,
    deleteFeeCategory,

    students,
    setStudents,
    addStudent,
    updateStudent,
    deleteStudent,

    parents,
    setParents,
    addParent,
    updateParent,
    deleteParent,

    courses,
    setCourses,
    addCourse,
    updateCourse,
    deleteCourse,

    schoolYears,
    setSchoolYears,
    addSchoolYear,
    updateSchoolYear,
    deleteSchoolYear,

    orders,
    setOrders,
    addOrder,
    updateOrder,

    orderItems,
    setOrderItems,

    questionnaires,
    setQuestionnaires,
    addQuestionnaire,
    updateQuestionnaire,
    deleteQuestionnaire,

    questionnaireFields,
    setQuestionnaireFields,
    addQuestionnaireField,
    updateQuestionnaireField,
    deleteQuestionnaireField,

    notifications,
    setNotifications,
    addNotification,
    updateNotification,
    deleteNotification,

    preAssessedFees,
    setPreAssessedFees,
    addPreAssessedFee,
    updatePreAssessedFee,
    deletePreAssessedFee,

    admins,
    setAdmins,

    adminUsers,
    setAdminUsers,
    addAdminUser,
    updateAdminUser,
    deleteAdminUser,

    communityMembers,
    setCommunityMembers,
    addCommunityMember,
    updateCommunityMember,
    deleteCommunityMember,

    feeDisclaimers,
    setFeeDisclaimers,
    addFeeDisclaimer,
    updateFeeDisclaimer,
    deleteFeeDisclaimer,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
